/**
 * Приоритеты правил доставки при перестановке (#2361, #2362).
 *
 * Одна формула для сервера (`DeliveryRulesRepository.reorder`) и для
 * оптимистичного обновления кэша в UI (`useReorderRoutingRules`): если они
 * считают по-разному, до повторной выборки кэш и БД лежат на разных шкалах.
 * Правила входящей маршрутизации стоят в том же диапазоне API
 * (`InboundRoutingRepository.planReorder`, #2851, #2873), но перечисленные
 * правила там меняются местами только между собой: порядок общий для всей
 * организации, и шаг этой шкалы — лишь первый из вариантов приоритетов.
 */

/** Шаг шкалы: между соседями остаётся место для правила, вставленного руками. */
export const DELIVERY_RULE_PRIORITY_STEP = 10;

/** Наибольший приоритет, который принимает API (create/update); перестановка за него не выходит. */
export const DELIVERY_RULE_PRIORITY_MAX = 10000;

/**
 * Шаг шкалы для проекта из `count` правил: `DELIVERY_RULE_PRIORITY_STEP`,
 * пока последнее правило укладывается в `DELIVERY_RULE_PRIORITY_MAX`, иначе
 * меньше (не менее 1) — чтобы правила проекта в 1001+ штук не получали
 * приоритет, который форма редактирования потом не принимает.
 */
export function deliveryRulePriorityStep(count: number): number {
  return Math.max(
    1,
    Math.min(
      DELIVERY_RULE_PRIORITY_STEP,
      Math.floor(DELIVERY_RULE_PRIORITY_MAX / Math.max(1, count))
    )
  );
}

/**
 * Новый порядок и приоритеты ВСЕХ правил проекта.
 *
 * Перечисленные в `orderedIds` идут первыми в заданном порядке, остальные —
 * следом в прежнем порядке: по приоритету, а при равенстве — в порядке, в
 * котором их отдал вызывающий (сортировка устойчивая; репозиторий отдаёт
 * равные приоритеты в порядке `priority, createdAt, id` — как их и исполняют,
 * вызывающий обязан передавать правила в этом порядке). Приоритет —
 * `(позиция + 1) * шаг`, где шаг — `deliveryRulePriorityStep` (10, а у проекта
 * с более чем 1000 правилами меньше), и не выше `DELIVERY_RULE_PRIORITY_MAX`.
 * Так неполный список не оставляет двух правил с одним приоритетом, а порядок
 * между ними не становится неопределённым. Только проект с более чем 10000
 * правилами упирается в потолок и получает равные приоритеты в хвосте — их
 * порядок задаёт тот же явный tie-breaker, что и при исполнении.
 *
 * Идентификаторы из `orderedIds`, которых нет среди `rules`, пропускаются;
 * повторы учитываются один раз (первое вхождение).
 */
export function rankDeliveryRules<T extends { id: string; priority: number }>(
  rules: readonly T[],
  orderedIds: readonly string[]
): Array<T & { priority: number }> {
  const byId = new Map(rules.map((rule) => [rule.id, rule]));
  const listed: T[] = [];
  const seen = new Set<string>();
  for (const id of orderedIds) {
    const rule = byId.get(id);
    if (!rule || seen.has(id)) continue;
    seen.add(id);
    listed.push(rule);
  }
  const rest = rules
    .filter((rule) => !seen.has(rule.id))
    .sort((a, b) => a.priority - b.priority);

  const step = deliveryRulePriorityStep(rules.length);
  return [...listed, ...rest].map((rule, index) => ({
    ...rule,
    priority: Math.min(DELIVERY_RULE_PRIORITY_MAX, (index + 1) * step),
  }));
}
